import axios from "axios";

/**
 * Create a new chat session for scenario editing
 * @param {string} userId - User ID
 * @param {string} title - Session title
 * @param {string} systemPrompt - Custom system prompt for the session
 * @param {Object} metadata - Additional metadata (scenario context, etc.)
 * @returns {Promise} Session creation response
 */
const createSession = (userId, title = 'Scenario Editing Session', systemPrompt = null, metadata = {}) =>
  axios.post("api/scenario-editor-ai/sessions", {
    userId,
    title,
    systemPrompt,
    metadata: {
      purpose: 'scenario-editing',
      ...metadata
    }
  });

/**
 * List chat sessions for a user (most recent first by default)
 * @param {string} userId - User ID
 * @param {Object} params - Query params (limit, offset, status, sort)
 * @returns {Promise} Sessions list
 */
const listSessions = (userId, params = {}) =>
  axios.get("api/scenario-editor-ai/sessions", {
    params: {
      userId,
      limit: 50,
      offset: 0,
      ...params,
    },
  });

/**
 * Send a message to the chatbot for scenario modification
 * @param {string} sessionId - Chat session ID
 * @param {string} content - User message
 * @param {string} userId - User ID
 * @param {Object} scenarioSnapshot - Current scenario state
 * @param {Object} options - Additional options
 * @param {number} options.maxRecentMessages - Maximum number of recent messages to include in context (default: backend config, recommend: 10-20)
 *   - Set to 0 to exclude ALL chat history (only current message sent to LLM)
 *   - Set to positive number to include that many recent messages
 * @returns {Promise} Assistant response
 *
 * BACKEND IMPLEMENTATION:
 * - Backend automatically strips scenario snapshots from old messages (only keeps most recent)
 * - Backend uses summarization for very old messages
 * - maxRecentMessages controls how many unsummarized messages to include
 *   - 0 = exclude all history (fresh context, minimal tokens)
 *   - Lower values (10-20) = less token usage, but less context
 *   - Higher values (30-50) = more context, but higher token usage
 */
const sendMessage = (sessionId, content, userId, scenarioSnapshot = null, options = {}) =>
  axios.post(`api/scenario-editor-ai/sessions/${sessionId}/messages`, {
    content,
    userId,
    scenarioSnapshot,
    // Control history inclusion: 0 = no history, positive number = that many recent messages
    maxRecentMessages: options.maxRecentMessages !== undefined ? options.maxRecentMessages : 10,
    requestId: options.requestId, // Pass requestId for progress tracking
  }, {
    signal: options.signal, // Support AbortController signal for cancellation
  });

/**
 * Send a message with file attachments to the chatbot
 * @param {string} sessionId - Chat session ID
 * @param {string} content - User message
 * @param {string} userId - User ID
 * @param {Object} scenarioSnapshot - Current scenario state
 * @param {File[]} files - Array of File objects to upload (PDF, DOCX, HTML, TXT)
 * @param {Object} options - Additional options
 * @param {number} options.maxRecentMessages - Maximum number of recent messages to include in context
 * @returns {Promise} Assistant response with attachment info
 */
const sendMessageWithAttachments = (sessionId, content, userId, scenarioSnapshot = null, files = [], options = {}) => {
  const formData = new FormData();

  // Add text fields
  formData.append('content', content);
  formData.append('userId', userId);

  if (scenarioSnapshot) {
    formData.append('scenarioSnapshot', JSON.stringify(scenarioSnapshot));
  }

  if (options.maxRecentMessages !== undefined) {
    formData.append('maxRecentMessages', options.maxRecentMessages.toString());
  }

  if (options.requestId) {
    formData.append('requestId', options.requestId);
  }

  // Add files
  files.forEach((file) => {
    formData.append('files', file);
  });

  return axios.post(
    `api/scenario-editor-ai/sessions/${sessionId}/messages/with-attachments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: options.signal, // Support AbortController signal for cancellation
    }
  );
};

/**
 * Get conversation history for a session
 * @param {string} sessionId - Chat session ID
 * @param {Object} params - Query parameters (includeInvalidated, limit, offset)
 * @returns {Promise} Conversation history
 */
const getMessages = (sessionId, params = {}) =>
  axios.get(`api/scenario-editor-ai/sessions/${sessionId}/messages`, {
    params: {
      includeInvalidated: false,
      limit: 100,
      offset: 0,
      ...params
    }
  });

/**
 * Get session details
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID
 * @returns {Promise} Session details
 */
const getSession = (sessionId, userId) =>
  axios.get(`api/scenario-editor-ai/sessions/${sessionId}`, {
    params: { userId }
  });

/**
 * Update session metadata
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID
 * @param {Object} updates - Updates (title, status, metadata)
 * @returns {Promise} Updated session
 */
const updateSession = (sessionId, userId, updates = {}) =>
  axios.patch(`api/scenario-editor-ai/sessions/${sessionId}`, {
    userId,
    ...updates
  });

/**
 * Delete/archive session
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID
 * @param {boolean} permanent - Whether to permanently delete
 * @returns {Promise} Delete confirmation
 */
const deleteSession = (sessionId, userId, permanent = false) =>
  axios.delete(`api/scenario-editor-ai/sessions/${sessionId}`, {
    params: { userId, permanent }
  });

/**
 * Create a snapshot of current session state
 * @param {string} sessionId - Chat session ID
 * @param {string} name - Snapshot name
 * @param {string} description - Snapshot description
 * @param {Object} metadata - Additional metadata
 * @returns {Promise} Snapshot creation response
 */
const createSnapshot = (sessionId, name, description = '', metadata = {}) =>
  axios.post(`api/scenario-editor-ai/sessions/${sessionId}/snapshots`, {
    name,
    description,
    metadata
  });

/**
 * Get all snapshots for a session
 * @param {string} sessionId - Chat session ID
 * @returns {Promise} List of snapshots
 */
const getSnapshots = (sessionId) =>
  axios.get(`api/scenario-editor-ai/sessions/${sessionId}/snapshots`);

/**
 * Restore session to a snapshot state
 * @param {string} sessionId - Chat session ID
 * @param {string} snapshotId - Snapshot ID
 * @param {boolean} invalidateLater - Whether to invalidate later messages
 * @returns {Promise} Restore confirmation
 */
const restoreSnapshot = (sessionId, snapshotId, invalidateLater = true) =>
  axios.post(`api/scenario-editor-ai/sessions/${sessionId}/snapshots/${snapshotId}/restore`, {
    invalidateLater
  });

const ApiScenarioChatbot = {
  createSession,
  listSessions,
  sendMessage,
  sendMessageWithAttachments,
  getMessages,
  getSession,
  updateSession,
  deleteSession,
  createSnapshot,
  getSnapshots,
  restoreSnapshot,
};

export default ApiScenarioChatbot;
